import { getCookie } from './cookie';

declare const process: { env: { REACT_APP_ENVIRONMENT?: string } };

const EXCLUDED_ROUTES = ['/reset-password'];

// The public site records the visitor's answer to the consent banner on the shared parent
// domain, so this application reads it instead of asking a second time behind the login.
// A missing answer counts as a refusal: it means the visitor has not been asked yet.
const AD_CONSENT_COOKIE_NAME = 'ssc_ad_consent';

function readAdConsent(): 'granted' | 'denied' {
    return getCookie(AD_CONSENT_COOKIE_NAME) === 'granted' ? 'granted' : 'denied';
}

export function initTracking(): void {
    if (process.env.REACT_APP_ENVIRONMENT !== 'prod') return;
    if (EXCLUDED_ROUTES.includes(window.location.pathname)) return;

    const adConsent = readAdConsent();

    // GA4. The consent default has to reach the queue before the Google tag
    // runs, so the queue is prepared first and the script appended after it.
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).gtag = function () {
        (window as any).dataLayer.push(arguments);
    };
    (window as any).gtag('consent', 'default', {
        ad_storage: adConsent,
        ad_user_data: adConsent,
        ad_personalization: adConsent,
        analytics_storage: adConsent
    });

    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-7L2HXJ80CQ';
    document.head.appendChild(gtagScript);

    (window as any).gtag('js', new Date());
    (window as any).gtag('config', 'G-7L2HXJ80CQ', {
        transport_url: 'https://ssgtm.ssc-app-services.com',
        first_party_collection: true
    });

    // Profitwell — the SDK reads its token from data-pw-auth on the #profitwell-js element
    const PW_AUTH = '8f31ef1e7705ac68fac2fe2419a49511';

    (window as any).profitwell =
        (window as any).profitwell ||
        function (...args: any[]) {
            ((window as any).profitwell.q = (window as any).profitwell.q || []).push(args);
        };

    const pwPlaceholder = document.createElement('script');
    pwPlaceholder.id = 'profitwell-js';
    pwPlaceholder.dataset.pwAuth = PW_AUTH;
    document.head.appendChild(pwPlaceholder);

    const pwScript = document.createElement('script');
    pwScript.async = true;
    pwScript.src = `https://public.profitwell.com/js/profitwell.js?auth=${PW_AUTH}`;
    document.head.appendChild(pwScript);

    (window as any).profitwell('start', {});

    // Reddit Pixel
    (window as any).rdt = (window as any).rdt || function (...args: any[]) {
        const p = (window as any).rdt;
        p.sendEvent ? p.sendEvent.apply(p, args) : p.callQueue.push(args);
    };
    (window as any).rdt.callQueue = (window as any).rdt.callQueue || [];

    const rdtScript = document.createElement('script');
    rdtScript.async = true;
    rdtScript.src = 'https://www.redditstatic.com/ads/pixel.js?pixel_id=a2_j06pryw0d13z';
    document.head.appendChild(rdtScript);

    (window as any).rdt('init', 'a2_j06pryw0d13z');
    (window as any).rdt('track', 'PageVisit');

    // OpenAI Ads. The queue holds calls until the SDK arrives and takes over.
    if (!(window as any).oaiq) {
        const queue: any = function () {
            queue.q.push(arguments);
        };
        queue.q = [];
        (window as any).oaiq = queue;
    }

    const openAiScript = document.createElement('script');
    openAiScript.async = true;
    openAiScript.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
    document.head.appendChild(openAiScript);

    (window as any).oaiq('consent', adConsent === 'granted');
    (window as any).oaiq('init', { pixelId: 'EqsShUysw3AounWNvioeiL' });

    // Bing UET
    (window as any).uetq = (window as any).uetq || [];
    (window as any).uetq.push('consent', 'default', { ad_storage: adConsent });

    const uetScript = document.createElement('script');
    uetScript.async = true;
    uetScript.onload = () => {
        // The UET instance has to replace window.uetq. It adopts whatever was queued
        // before the tag loaded, and from then on its own push() is what transmits.
        // Leaving the plain array in place silently drops every later event.
        (window as any).uetq = new (window as any).UET({
            ti: '5318140',
            enableAutoSpaTracking: true,
            q: (window as any).uetq
        });
        (window as any).uetq.push('pageLoad');
    };
    uetScript.src = 'https://bat.bing.net/bat.js?ti=5318140';
    document.head.appendChild(uetScript);
}
